<template>
  <div>
    <section class="activity-stage">
      <div class="main">
        <aside>
          <img :src="currentRound.asideImg" />
        </aside>
        <div class="steps">
          <div class="bubble" :style="quoteBubbleImage">
            <blinking-cursor-text
              v-if="currentRound && currentRound.steps && currentRound.steps[0].needType"
              :text="currentRound.steps[0].answer"
              :text-step="0"
              :class-name="bubbleTextClass"
            ></blinking-cursor-text>
          </div>
          <div class="paragraph" v-show="isNextStep">
            <img :class="{ isMobile }" :src="currentRound.mainImg" />
            <blinking-cursor-text
              v-if="isNextStep"
              :text="currentRound.steps[1].answer"
              :text-step="1"
            ></blinking-cursor-text>
          </div>
        </div>
      </div>
    </section>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TimelineLite, TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import quotebubble from 'assets/svg/quotebubble.svg';

export default {
  components: {
    BlinkingCursorText
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'QuoteBubble2',
      quoteBubbleImage: {
        background: `url('data:image/svg+xml;utf8,${quotebubble}') left top no-repeat`
      },
      isNextStep: false
    };
  },

  computed: {
    bubbleTextClass() {
      if (!this.currentRound || !this.currentRound.steps) return '';
      return this.currentRound.steps[0].answer.length > 130 ? 'smaller' : '';
    }
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  watch: {
    isNextStep() {
      TweenMax.fromTo('.paragraph', 0.8, { autoAlpha: 0 }, { autoAlpha: 1 });
    }
  },

  methods: {
    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type the information.',
          voice: `${this.voiceInstructionsPrefix}/tellmemore-${this.getRandomVoice()}-1.mp3`,
          delay: 4
        }
      };
    },

    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);

      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(r => {
        if (r.asideImg || r.mainImg) this.queueImage(r.asideImg, r.mainImg);
      });

      return this.downloadImages();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        hideNumberRow: false,
        roofs
      };
    },

    startActivity() {
      this.errorLoggerStart();
      this.setRound();
    },

    setRound() {
      this.setupNewRound();
      this.enableKeyboard();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.incrementTypingIndex();
      this.sndType();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.setCurrentStep();
        if (this.progress.stepIndex > 0) {
          this.isNextStep = true;
        }
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      if (this.isLastRound) {
        TweenMax.delayedCall(0.8, this.completeActivity);
      } else {
        const t = new TimelineLite();

        t.delay(1)
          .to('.activity-stage', 0.8, { opacity: 0 })
          .call(this.setRound);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.main {
  display: flex;
  box-sizing: border-box;
  padding: 15px 10px 0;
  justify-content: space-between;

  aside {
    display: block;
    height: 350px;
    margin-top: 40px;
    width: 18%;

    img {
      height: auto;
      width: 80%;
    }
  }

  .steps {
    width: 80%;
    .bubble {
      width: 760px;
      height: 130px;
      margin-top: 15px;
      font-size: 150%;
      p {
        padding: 15px;

        &.smaller {
          font-size: 90%;
        }
      }
    }
    .paragraph {
      text-align: left;
      img {
        display: block;
        height: auto;
        width: 50%;

        &.isMobile {
          width: 200px !important;
        }
      }
      p {
        font-size: 130%;
        margin: 1%;
      }
    }
  }
}
</style>
